import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  CLIENT_ACTIVITY_EVENT_NAME,
  recordClientActivitySnapshot,
} from '@/shared/observability/clientActivity';

type UseClientActivityMonitorArgs = {
  authLoading: boolean;
  hasSession: boolean;
  enabled?: boolean;
};

type PerformanceWithMemory = Performance & {
  memory?: {
    usedJSHeapSize?: number;
    totalJSHeapSize?: number;
  };
};

const sampleIntervalMs = 15_000;

const roundMb = (value: number | undefined): number | null => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return null;
  }
  return Math.round((value / (1024 * 1024)) * 10) / 10;
};

export const useClientActivityMonitor = ({
  authLoading,
  hasSession,
  enabled = import.meta.env.DEV,
}: UseClientActivityMonitorArgs): void => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const notificationStreamStateRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const handleActivityEvent = (event: Event): void => {
      const detail = (event as CustomEvent<{ event?: unknown; state?: unknown }>).detail;
      if (detail.event !== 'notifications_stream_state') {
        return;
      }
      notificationStreamStateRef.current =
        typeof detail.state === 'string' ? detail.state : notificationStreamStateRef.current;
    };

    window.addEventListener(CLIENT_ACTIVITY_EVENT_NAME, handleActivityEvent);
    return () => {
      window.removeEventListener(CLIENT_ACTIVITY_EVENT_NAME, handleActivityEvent);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;

    const recordSnapshot = (): void => {
      const queries = queryClient.getQueryCache().getAll();
      const activeQueries = queries.filter((query) => query.getObserversCount() > 0);
      const performanceValue = window.performance as PerformanceWithMemory;
      recordClientActivitySnapshot({
        ts_ms: Date.now(),
        path: `${location.pathname}${location.search}${location.hash}`,
        visibility: document.visibilityState === 'visible' ? 'visible' : 'hidden',
        auth_loading: authLoading,
        has_session: hasSession,
        query_total: queries.length,
        query_active: activeQueries.length,
        query_fetching: queryClient.isFetching(),
        mutation_active: queryClient.isMutating(),
        notification_stream_state: notificationStreamStateRef.current,
        used_js_heap_mb: roundMb(performanceValue.memory?.usedJSHeapSize),
        total_js_heap_mb: roundMb(performanceValue.memory?.totalJSHeapSize),
      });
    };

    recordSnapshot();
    const intervalId = window.setInterval(recordSnapshot, sampleIntervalMs);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    authLoading,
    enabled,
    hasSession,
    location.hash,
    location.pathname,
    location.search,
    queryClient,
  ]);
};
